import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
	constants,
	Action,
	ACTION_CODES,
	ACTION_GROUP_CODES,
	LocalStorageService,
	Permissions,
	APPLICATION_ACTIONS
} from '@edassist/common';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';
import { AuthenticationService } from '../auth/auth.service';

@Injectable({ providedIn: 'root' })
export class PermissionService {
	userPermissions: Observable<Array<Action>>;
	private readonly relativeUrl = 'users-types/';
	private readonly applicationsUrl = 'applications/';
	private readonly userPermissionsSubject: BehaviorSubject<Array<Action>>;

	constructor(
		private readonly http: HttpClient,
		private readonly injector: Injector
	) {
		this.userPermissionsSubject = new BehaviorSubject<Array<Action>>(
			LocalStorageService.getItem(constants.localStorageKeys.PERMISSIONS_USER)
		);
		this.userPermissions = this.userPermissionsSubject.asObservable();
	}

	private get authService(): AuthenticationService {
		return this.injector.get(AuthenticationService);
	}

	getUserPermissions(
		...groupCodes: Array<ACTION_GROUP_CODES>
	): Observable<Array<Action>> {
		const httpParams = HttpUtilityService.toCommaSeparatedHttpParams(
			groupCodes,
			'groupCode'
		);

		return this.http
			.get<
				Array<Action>
			>(`${this.relativeUrl}${this.authService.currentUserValue.userTypeId}/actions`, httpParams)
			.pipe(
				catchError(() => of([])),
				map((actions) => {
					// Keep app-wide level user permissions at the local storage level to maintain these across browser tabs/sessions
					LocalStorageService.setItem(
						constants.localStorageKeys.PERMISSIONS_USER,
						actions
					);
					this.userPermissionsSubject.next(actions);

					return actions;
				})
			);
	}

	getApplicationPermissions(
		applicationId: number,
		groupCode: ACTION_GROUP_CODES
	): Observable<Permissions> {
		const httpParamOptions: HttpParamObject =
			HttpUtilityService.toPlainHttpParams({
				groupCode
			});

		return this.http
			.get<
				Array<Action>
			>(`${this.applicationsUrl}${applicationId}/actions`, httpParamOptions)
			.pipe(
				// tslint:disable-next-line:no-unnecessary-callback-wrapper
				catchError((error) => of(error)),
				map(
					(actions) =>
						Array.isArray(actions) &&
						actions.reduce(
							(accumulator: Permissions, currentAction: Action) => {
								accumulator[ACTION_CODES[currentAction.code]] = true;

								return accumulator;
							},
							{}
						)
				)
			);
	}

	getApplicationActions(
		applicationId: number,
		groupCode: ACTION_GROUP_CODES
	): Observable<Array<Action>> {
		return this.http.get<Array<Action>>(
			`${this.applicationsUrl}${applicationId}/actions`,
			{
				params: { groupCode }
			}
		);
	}

	getUserTypePermissionsForAppeals(
		...groupCodes: Array<ACTION_GROUP_CODES>
	): Observable<Array<Action>> {
		const httpParams = HttpUtilityService.toCommaSeparatedHttpParams(
			groupCodes,
			'groupCode'
		);

		return this.http.get<Array<Action>>(
			`${this.relativeUrl}${this.authService.currentUserValue.userTypeId}/actions`, httpParams
		);
	}

	isActionAllowed(actionCode: APPLICATION_ACTIONS): Observable<boolean> {
		return this.http.get<boolean>(`actions/${actionCode}/is-action-allowed`);
	}
}
